/**
 * Single Row Orchestrator
 *
 * Coordinates rendering of single-row multi-unit buildings.
 * Uses edge-based wall rendering with proper deduplication.
 */

import * as THREE from 'three'
import { FEET_TO_METERS } from '@/constants/units'
import type { GeneratedBuilding, GeneratedUnit } from '@/lib/templates/types'
import { renderUnits, type UnitRenderOptions } from '../composers/RectangularUnitComposer'

/**
 * Building render options for single-row
 */
export interface SingleRowRenderOptions extends UnitRenderOptions {
  /** Building ID for userData */
  buildingId?: string
  /** Building name for userData */
  buildingName?: string
  /** Position in feet [x, y, z] relative to scene center */
  position?: [number, number, number]
  /** Rotation in degrees */
  rotationDeg?: number
}

/**
 * Render a single-row building from generated config
 *
 * Uses the template's generated building and units data.
 * Units are positioned according to their offset_x values.
 */
export function renderSingleRowBuilding(
  building: GeneratedBuilding,
  units: GeneratedUnit[],
  options: SingleRowRenderOptions = {}
): THREE.Group {
  const {
    buildingId,
    buildingName,
    position = [0, 0, 0],
    rotationDeg,
    ...unitOptions
  } = options

  const group = new THREE.Group()
  group.name = `building-${buildingId || building.code}`
  group.userData = {
    buildingId: buildingId || building.code,
    buildingName: buildingName || building.name,
    buildingType: building.type,
    template: 'single_row',
    unitCount: units.length,
  }

  // Get colors from building settings
  const floorColor = building.settings?.floor_color || unitOptions.floorColor
  const wallColor = building.settings?.wall_color || unitOptions.wallColor

  // Render all units using the composer
  // The composer handles edge-based wall rendering with proper deduplication
  const unitsGroup = renderUnits(units, {
    ...unitOptions,
    floorColor,
    wallColor,
  })

  // Shift units to center them on building origin
  // Units are positioned from the west edge (offset_x = 0 is at west wall)
  // We need to shift them so the building center is at origin
  const buildingCenterX = building.exterior_width / 2
  unitsGroup.position.x = -buildingCenterX * FEET_TO_METERS

  group.add(unitsGroup)

  // Apply building rotation
  const rotation = rotationDeg ?? (building.rotation * 180 / Math.PI)  // Convert radians to degrees
  if (rotation !== 0) {
    group.rotation.y = (rotation * Math.PI) / 180  // Convert back to radians
  }

  // Apply building position (convert feet to meters)
  group.position.set(
    position[0] * FEET_TO_METERS,
    position[1] * FEET_TO_METERS,
    position[2] * FEET_TO_METERS
  )

  return group
}

/**
 * Render a single-row building from config object
 * Convenience wrapper for database-loaded data
 */
export function renderSingleRowFromConfig(
  config: {
    building: GeneratedBuilding
    units: GeneratedUnit[]
    position: [number, number, number]
    rotation?: number
    buildingId?: string
  },
  options: Omit<SingleRowRenderOptions, 'position' | 'rotationDeg' | 'buildingId'> = {}
): THREE.Group {
  return renderSingleRowBuilding(
    config.building,
    config.units,
    {
      ...options,
      buildingId: config.buildingId,
      buildingName: config.building.name,
      position: config.position,
      rotationDeg: config.rotation,
    }
  )
}
